import * as React from 'react'
import { Button } from 'primereact/button'
import DialogCustom from 'app/shared/components/Dialog'
/**
@description
   - 
*/

type PickerMode = 'year' | 'month' | 'yearMonth'

export interface Props {
    /**
     * Picker 표시 모드
     * 'year' - 년도만 표시, 'month' - 월만 표시, 'yearMonth' - 년월 표시
     */
    mode: PickerMode
    /**
     * 표시할 년도 리스트 데이터
     */
    years?: Array<string>
    /**
     * 설정할 년도 데이터, years의 데이터 중 하나
     */
    selectedYear?: string
    /**
     * 표시할 월 리스트 데이터
     */
    months?: Array<string>
    /**
     * 설정한 월 데이터, months의 데이터 중 하나
     */
    selectedMonth?: string
    /**
     * Picker표시 여부
     */
    visible: boolean
    /**
     * pickerId 포커스를 위해 사용. 없는 경우 closeFocus처리를 하지 않는다.
     */
    pickerId?: string
    dialogClassName?: string
    dialogHeaderName: string
    handlePlaceholder: string
    handleClassName?: string
    onOpen?: () => void
    onClose?: () => void
    onSelectYear?: (year: string) => void
    onSelectMonth?: (month: string) => void
    onSelectYearMonth?: (year?: string, month?: string) => void
}

const itemHeight = 40
const dummyCount = 2

const Picker: React.FC<Props> = ({
    mode,
    years,
    selectedYear = '',
    months,
    selectedMonth = '',
    visible,
    pickerId,
    dialogClassName,
    dialogHeaderName,
    handlePlaceholder,
    handleClassName,
    onOpen,
    onClose,
    onSelectYear,
    onSelectMonth,
    onSelectYearMonth,
}) => {
    const [handle, setHandle] = React.useState(visible)
    const getSelectIndex = (scrollTop: number) =>
        Math.floor(scrollTop / (itemHeight * 0.95))
    const yearRef = React.useRef<HTMLDivElement>(null)
    const monthRef = React.useRef<HTMLDivElement>(null)
    const [yearIndex, setYearIndex] = React.useState(0)
    const [monthIndex, setMonthIndex] = React.useState(0)
    const [selectYear, setSelectYear] = React.useState(selectedYear)
    const [selectMonth, setSelectMonth] = React.useState(selectedMonth)

    // 상,하 여백 주기
    const renderItem = (list: string[], selectedIndex: number) => {
        const items = []
        for (let i = 0; i < dummyCount; i++) {
            items.push(
                <div key={`dummy-top-${i}`} className="picker-item dummy"></div>
            )
        }
        list.forEach((item, idx) => {
            const isSelected = idx === selectedIndex
            items.push(
                <div
                    key={idx}
                    className={`picker-item ${
                        isSelected ? '-selected' : ''
                    }`.trim()}
                >
                    {item}
                </div>
            )
        })
        for (let i = 0; i < dummyCount; i++) {
            items.push(
                <div
                    key={`dummy-bottom-${i}`}
                    className="picker-item dummy"
                ></div>
            )
        }
        return items
    }

    // 스크롤 items index 체크
    const handleScroll = (
        ref: React.RefObject<HTMLDivElement>,
        list: string[],
        setIndex: React.Dispatch<React.SetStateAction<number>>
    ) => {
        const scrollTop = ref.current?.scrollTop || 0
        const index = getSelectIndex(scrollTop)
        setIndex(Math.min(Math.max(index, 0), list.length - 1))
    }

    // 선택
    const [handleSelected, setHandleSelected] = React.useState('')
    const [resultDate, setResultDate] = React.useState('')
    const handleSelect = () => {
        let selectYearString: string | undefined = undefined
        let selectMonthString: string | undefined = undefined
        if (mode === 'year' || mode === 'yearMonth') {
            const yearScrollTop = yearRef.current?.scrollTop || 0
            const yearIndex = getSelectIndex(yearScrollTop)
            const selectYear =
                years[Math.min(years.length - 1, Math.max(0, yearIndex))]
            setSelectYear(selectYear)
            onSelectYear?.(years[yearIndex])
            selectYearString = years[yearIndex]
            setResultDate(
                `${selectYear === '0년' ? '' : selectYear}` +
                    (selectYear !== '' && selectYear !== '0년' ? ' ' : '') +
                    `${selectMonth === '0개월' ? '' : selectMonth}`
            )
        }
        if (mode === 'month' || mode === 'yearMonth') {
            const monthScrollTop = monthRef.current?.scrollTop || 0
            const monthIndex = getSelectIndex(monthScrollTop)
            const selectMonth =
                months[Math.min(months.length - 1, Math.max(0, monthIndex))]
            setSelectMonth(selectMonth)
            onSelectMonth?.(months[monthIndex])
            selectMonthString = months[monthIndex]
            setResultDate(
                `${selectYear === '0년' ? '' : selectYear}` +
                    (selectYear !== '' && selectYear !== '0년' ? ' ' : '') +
                    `${selectMonth === '0개월' ? '' : selectMonth}`
            )
        }
        onSelectYearMonth?.(selectYearString, selectMonthString)
        setHandleSelected('-on')
        onClose()
    }

    // 상태 확인
    React.useEffect(() => {
        setHandle(visible)
        setTimeout(() => {
            if (mode === 'year' || mode === 'yearMonth') {
                const yearIndex = years.indexOf(selectYear)
                if (yearRef.current && yearIndex >= 0) {
                    yearRef.current.scrollTo({
                        top: yearIndex * itemHeight,
                        behavior: 'auto',
                    })
                }
            }
            if (mode === 'month' || mode === 'yearMonth') {
                const monthIndex = months.indexOf(selectMonth)
                if (monthRef.current && monthIndex >= 0) {
                    monthRef.current.scrollTo({
                        top: monthIndex * itemHeight,
                        behavior: 'auto',
                    })
                }
            }
        }, 80)
        setResultDate(
            `${selectYear === '0년' ? '' : selectYear}` +
                (selectYear !== '' && selectYear !== '0년' ? ' ' : '') +
                `${selectMonth === '0개월' ? '' : selectMonth}`
        )
    }, [visible, selectYear, selectMonth])

    React.useEffect(() => {
        setHandleSelected(
            (selectedYear + selectedMonth).trim().length > 0 ? '-on' : ''
        )
        setSelectYear(selectedYear)
        setSelectMonth(selectedMonth)
    }, [selectedYear, selectedMonth])

    return (
        <>
            <div className={`${handleClassName} ${handleSelected}`.trim()}>
                <Button
                    {...(pickerId && { id: pickerId })}
                    className="input"
                    onClick={onOpen}
                >
                    {resultDate?.trim() ? resultDate : handlePlaceholder}
                </Button>
            </div>

            <DialogCustom
                {...(pickerId && { closeFocus: pickerId })}
                visible={handle}
                header={dialogHeaderName}
                className={`bottom-sheet ${dialogClassName}`.trim()}
                onClose={onClose}
                footer={
                    <Button
                        label="선택"
                        className="button-basic-primary -lg width-full"
                        onClick={handleSelect}
                    />
                }
            >
                <div
                    className={`picker-container ${
                        mode === 'yearMonth' ? '' : '-single'
                    }`.trim()}
                >
                    {(mode === 'year' || mode === 'yearMonth') && (
                        <div
                            className="picker-column -year"
                            ref={yearRef}
                            onScroll={() =>
                                handleScroll(yearRef, years, setYearIndex)
                            }
                            onClick={(e) => {}}
                        >
                            {renderItem(years, yearIndex)}
                        </div>
                    )}
                    {(mode === 'month' || mode === 'yearMonth') && (
                        <div
                            className="picker-column -month"
                            ref={monthRef}
                            onScroll={() =>
                                handleScroll(monthRef, months, setMonthIndex)
                            }
                        >
                            {renderItem(months, monthIndex)}
                        </div>
                    )}
                    <div className="picker-selected"></div>
                </div>
            </DialogCustom>
        </>
    )
}

export default Picker
